import {Injectable} from '@angular/core';

/**
 * Interface for media items used in slideshows
 */
export interface MediaItem {
  type: 'image' | 'video';
  url: string;
  duration?: number; // Duration in seconds for videos
}

/**
 * Service for calculating timing-related values for media items in slideshows
 */
@Injectable({
  providedIn: 'root'
})
export class MediaTimingService {
  // Default time per image slide in seconds
  private readonly DEFAULT_IMAGE_DURATION = 5; // seconds per image
  // Maximum video duration to consider when calculating times
  private readonly MAX_VIDEO_DURATION = 30; // 30 seconds maximum per video
  // Default fallback duration if no media items provided
  private readonly DEFAULT_FALLBACK_DURATION = 20; // 20 seconds default

  /**
   * Calculate the end time for audio playback based on media items
   *
   * @param mediaItems Array of media items (images and videos)
   * @param startTime Start time of the audio track (defaults to 0)
   * @param providedEndTime Optional end time that may be provided by the API
   * @returns Calculated end time in seconds
   */
  calculateEndTime(mediaItems: MediaItem[], startTime: number = 0, providedEndTime?: number): number {
    let baseEndTime: number;

    // If end time is already provided, use it as the base
    if (providedEndTime && providedEndTime > 0) {
      baseEndTime = providedEndTime;
    } else if (!mediaItems || mediaItems.length === 0) {
      // If no media items, use default duration from start time
      baseEndTime = startTime + this.DEFAULT_FALLBACK_DURATION;
    } else {
      // Count images and calculate their total duration
      const imageCount = mediaItems.filter(item => item.type === 'image').length;
      const imageDuration = imageCount * this.DEFAULT_IMAGE_DURATION;

      // Calculate duration for video items, capping each at MAX_VIDEO_DURATION
      const videoItems = mediaItems.filter(item => item.type === 'video');
      const videoDuration = videoItems.reduce((total, video) => {
        const duration = video.duration && video.duration > 0
          ? Math.min(video.duration, this.MAX_VIDEO_DURATION)
          : this.MAX_VIDEO_DURATION;
        return total + duration;
      }, 0);

      // Total duration is the sum of image and video durations
      const totalDuration = imageDuration + videoDuration;

      // If no content found or duration is less than minimum, use default
      baseEndTime = (totalDuration <= 0 || totalDuration < this.DEFAULT_FALLBACK_DURATION)
        ? startTime + this.DEFAULT_FALLBACK_DURATION
        : startTime + totalDuration;
    }

    // Single-video override (normal mode): when exactly one media item is a
    // video with a known real length, the song clip must be at least as long
    // as the video. The clip plays from start_time, so the end must reach
    // start_time + video length (not the raw length, which would shorten the
    // clip whenever start_time > 0). This also bypasses the MAX_VIDEO_DURATION
    // cap for this case. start_time itself is untouched.
    if (
      mediaItems &&
      mediaItems.length === 1 &&
      mediaItems[0].type === 'video' &&
      mediaItems[0].duration &&
      mediaItems[0].duration > 0
    ) {
      return Math.max(baseEndTime, startTime + mediaItems[0].duration);
    }

    return baseEndTime;
  }
}
